import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Alert, InlineLoader } from '../../components/ui'
import { fetchListingHistory } from '../../api/objects'
import type { ListingChange, Verification } from '../../api/objects'
import { keys } from '../../api/keys'
import { t } from '../../i18n'
import { fieldValue, fullTime } from '../../format'
import {
  changeFieldLabel,
  changeSourceLabel,
  verificationFactLabel,
  verificationMethodLabel,
} from '../../labels'

/** How far back one opening looks. Enough for "what did we quote them last week". */
const LIMIT = 50

/**
 * What this offer used to be, and who checked it.
 *
 * Both halves answer the same question — what has happened to this listing — so
 * they arrive in one request and open together. A manager comparing what we sent
 * last week against what is true today needs the price history; the exposure
 * rule needs the same rows, and the checks beside them say whether anybody has
 * confirmed the current figures at all.
 *
 * Fetched only when somebody opens it: most listings are read without anybody
 * asking, and a request per listing on a property with six of them buys nothing.
 */
export function ListingHistory({ listingId }: { readonly listingId: string }) {
  const [open, setOpen] = useState(false)

  const history = useQuery({
    queryKey: keys.listingHistory(listingId),
    queryFn: ({ signal }) => fetchListingHistory(listingId, LIMIT, signal),
    enabled: open,
  })

  return (
    <details
      className="listing-history"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="listing-history__summary">{t('history.summary')}</summary>

      <div className="listing-history__body">
        {history.isPending && open ? <InlineLoader /> : null}
        {history.isError ? <Alert tone="error">{t('history.failed')}</Alert> : null}

        {history.data === undefined ? null : (
          <>
            <h4 className="listing-history__title">{t('history.changes')}</h4>
            {history.data.changes.length === 0 ? (
              <p className="panel-block__empty">{t('history.changesEmpty')}</p>
            ) : (
              <ul className="change-list">
                {history.data.changes.map((change) => (
                  <ChangeRow key={change.id} change={change} />
                ))}
              </ul>
            )}

            <h4 className="listing-history__title">{t('history.verifications')}</h4>
            {history.data.verifications.length === 0 ? (
              <p className="panel-block__empty">{t('history.verificationsEmpty')}</p>
            ) : (
              <ul className="change-list">
                {history.data.verifications.map((verification) => (
                  <VerificationRow key={verification.id} verification={verification} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </details>
  )
}

function ChangeRow({ change }: { readonly change: ListingChange }) {
  return (
    <li className="change">
      <div className="change__head">
        <span className="change__field">{changeFieldLabel(change.field)}</span>
        <span className="change__time">{fullTime(change.changedAt)}</span>
      </div>
      <div className="change__values">
        {/*
          Both halves, always. "The rent is now 750" is not the fact a manager
          needs when the client was quoted 700 last week; the pair is.
        */}
        <span className="change__was">
          {t('history.was')}: {fieldValue(change.oldValue)}
        </span>
        <span className="change__became">
          {t('history.became')}: {fieldValue(change.newValue)}
        </span>
      </div>
      <div className="change__meta">
        <span>{changeSourceLabel(change.source)}</span>
        {change.note === '' ? null : <span>{change.note}</span>}
      </div>
    </li>
  )
}

function VerificationRow({ verification }: { readonly verification: Verification }) {
  return (
    <li className="change">
      <div className="change__head">
        <span className="change__field">{verificationMethodLabel(verification.method)}</span>
        <span className="change__time">{fullTime(verification.verifiedAt)}</span>
      </div>
      <div className="change__values">
        <span>{t('freshness.until', { time: fullTime(verification.expiresAt) })}</span>
        {verification.factsConfirmed.length === 0 ? null : (
          <span>
            {t('freshness.facts', {
              facts: verification.factsConfirmed.map(verificationFactLabel).join(', '),
            })}
          </span>
        )}
      </div>
      <div className="change__meta">
        {/*
          The evidence itself, not a tick. Somebody re-reading this a month later
          is asking where the proof was, and a check whose evidence is not on
          screen is a check nobody can re-examine.
        */}
        <span className="change__evidence">{verification.evidence}</span>
        {verification.note === '' ? null : <span>{verification.note}</span>}
      </div>
    </li>
  )
}
